import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { Button } from "@/components/ui/button.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.jsx";
import { LoadingSpinner } from "@/components/ui/loading.jsx";
import { isRequestAborted } from "@/api/api-utils.js";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import {
  tenantAvatarColorClass,
  tenantInitials,
  tenantLogoImageSrc,
} from "@/lib/tenant-branding-utils.js";
import {
  getDetechtionLogoSrc,
  isEnbaseTenant,
  resolveEnbaseTenantId,
} from "@/lib/detechtion-branding.js";
import { useLogoAppearance } from "@/hooks/useLogoAppearance.js";
import { useSidebarPortalContainer } from "@/components/sidebar-portal.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";

/**
 * Container treatment per logo appearance. Background only — no border so
 * full-bleed wordmarks (e.g. Enbase) are not clipped against a hairline frame.
 * Near-white marks on transparent backgrounds (e.g. Kodiak) still get a
 * primary tile so they remain visible on the sidebar.
 * @param {import("@/hooks/useLogoAppearance.js").LogoAppearance} appearance
 */
function logoContainerClass(appearance) {
  return appearance === "light-on-transparent"
    ? "bg-primary"
    : "bg-transparent";
}

/** Sidebar header size box shared by Enbase wordmark and no-tenant fallback. */
const DETECHTION_HEADER_SIZE =
  "w-full max-w-[14rem] aspect-[2/1] max-h-[11rem] min-h-[4.5rem] shrink-0";

/**
 * Theme-aware Detechtion.AI wordmark image (fills its size placeholder).
 * @param {{ className?: string, imgClassName?: string }} props
 */
function DetechtionWordmarkImage({ className, imgClassName }) {
  const { theme } = useTheme();
  const src = getDetechtionLogoSrc(theme);
  return (
    <div
      className={cn(
        "flex items-center justify-center overflow-hidden bg-transparent",
        className
      )}>
      <img
        src={src}
        alt='Detechtion.AI'
        className={cn(
          "h-full w-full min-h-0 object-contain object-center",
          imgClassName
        )}
      />
    </div>
  );
}

/**
 * Leading avatar: logo, else initials on colored tile.
 * @param {{ tenant: import("@/api/detechtion-account-api.js").NormalizedTenant, sizeClass?: string }} props
 */
function TenantAvatar({ tenant, sizeClass = "h-9 w-9" }) {
  const [imgFailed, setImgFailed] = useState(false);
  const logoSrc = tenantLogoImageSrc(tenant);
  const appearance = useLogoAppearance(logoSrc);
  const seed = `${tenant.tenantId}:${tenant.displayName}:${logoSrc ?? ""}`;
  const initials = tenantInitials(tenant.displayName);
  const colorClass = tenantAvatarColorClass(seed);

  const initialsTypography = sizeClass.includes("h-8")
    ? "text-xs font-semibold"
    : "text-3xl font-semibold";

  if (logoSrc && !imgFailed) {
    return (
      <div
        className={cn(
          sizeClass.includes("h-8") ? "rounded-md" : "rounded-lg",
          "overflow-hidden",
          logoContainerClass(appearance),
          sizeClass
        )}>
        <img
          src={logoSrc}
          alt=''
          className='h-full w-full min-h-0 object-contain object-center'
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        sizeClass.includes("h-8") ? "rounded-md" : "rounded-lg",
        "flex items-center justify-center shrink-0",
        initialsTypography,
        colorClass,
        sizeClass
      )}>
      {initials}
    </div>
  );
}

/**
 * Sidebar tenant row / header mark: Detechtion logo for Enbase tenant, else {@link TenantAvatar}.
 * @param {{ tenant: import("@/api/detechtion-account-api.js").NormalizedTenant, sizeClass: string, roundedClass?: string, enbaseTenantId?: number|null, compact?: boolean }} props
 */
function TenantSwitcherMark({
  tenant,
  enbaseTenantId,
  sizeClass,
  roundedClass = "rounded-md",
  compact = false,
}) {
  if (isEnbaseTenant(tenant, enbaseTenantId)) {
    return (
      <DetechtionWordmarkImage
        className={cn(roundedClass, "shrink-0", sizeClass)}
        imgClassName={compact ? "p-0.5" : undefined}
      />
    );
  }
  return <TenantAvatar tenant={tenant} sizeClass={sizeClass} />;
}

/**
 * Sidebar tenant switcher control (mockup: bordered SWITCH TENANT + cyan chevron).
 * @returns {JSX.Element}
 */
export default function TenantSwitcher() {
  const { availableTenants, currentTenant, changeSessionTenant } =
    useSessionTenant();

  const [location, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(/** @type {number|null} */ (null));
  const sidebarPortalContainer = useSidebarPortalContainer();

  const enbaseTenantId = useMemo(
    () => resolveEnbaseTenantId(availableTenants),
    [availableTenants]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableTenants
      .filter((t) => (q ? t.displayName.toLowerCase().includes(q) : true))
      .sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, {
          sensitivity: "base",
        })
      );
  }, [availableTenants, search]);

  const active = currentTenant;

  const handlePick = async (tenantId) => {
    if (tenantId === currentTenant?.tenantId) {
      setOpen(false);
      return;
    }
    setBusyId(tenantId);
    try {
      await changeSessionTenant(tenantId);
      setOpen(false);
      setSearch("");
      // Asset detail routes are keyed by the previous tenant's asset id, which
      // won't resolve under the new tenant's fleet; send the user to the root
      // so they land on the dashboard instead of a blank screen.
      if (location.startsWith("/asset/")) {
        setLocation("/");
      }
    } catch (e) {
      if (!isRequestAborted(e)) {
        console.error("Failed to change tenant:", e);
      }
    } finally {
      setBusyId(null);
    }
  };

  if (availableTenants.length === 0) {
    return (
      <div className='flex items-center gap-2 min-h-[40px] px-1'>
        <p className='text-sm font-light text-muted-foreground'>
          No tenants available.
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <div
        className='flex justify-center w-full'
        title={
          active?.displayName
            ? availableTenants.length > 1
              ? `${active.displayName} — open menu to switch`
              : active.displayName
            : "Select a tenant"
        }>
        {active ? (
          <TenantSwitcherMark
            key={active.tenantId}
            tenant={active}
            enbaseTenantId={enbaseTenantId}
            sizeClass={DETECHTION_HEADER_SIZE}
            roundedClass='rounded-lg'
          />
        ) : (
          <DetechtionWordmarkImage
            key='no-tenant'
            className={cn("rounded-lg", DETECHTION_HEADER_SIZE)}
          />
        )}
      </div>

      {availableTenants.length > 1 ? (
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch("");
          }}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              className={cn(
                "w-full justify-between px-3 py-2.5 h-auto rounded-md",
                "border-sidebar-border bg-sidebar text-muted-foreground",
                "hover:bg-muted hover:text-foreground font-medium text-xs tracking-wide uppercase"
              )}
              type='button'
              title={
                active?.displayName
                  ? `${active.displayName} — open menu to switch`
                  : "Select a tenant"
              }>
              <span>{active ? "Switch tenant" : "Select tenant"}</span>
              <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            {...(sidebarPortalContainer
              ? { container: sidebarPortalContainer }
              : {})}
            align='start'
            className='w-[min(20rem,calc(100vw-2rem))] p-0 border-border shadow-md'>
            <div className='border-b border-border px-3 py-2'>
              <p className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
                Select tenant
              </p>
              <input
                type='search'
                placeholder='Search…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='mt-2 w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:border-primary'
              />
            </div>
            <div className='max-h-[min(18rem,var(--radix-popover-content-available-height))] overflow-y-auto py-1'>
              {rows.length === 0 ? (
                <p className='px-3 py-4 text-sm text-muted-foreground text-center'>
                  No tenants found.
                </p>
              ) : (
                rows.map((t) => {
                  const selected = t.tenantId === currentTenant?.tenantId;
                  const busy = busyId === t.tenantId;
                  return (
                    <button
                      key={t.tenantId}
                      type='button'
                      disabled={busy}
                      onClick={() => handlePick(t.tenantId)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors",
                        selected
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted text-foreground"
                      )}>
                      <TenantSwitcherMark
                        tenant={t}
                        enbaseTenantId={enbaseTenantId}
                        sizeClass='h-8 w-11 shrink-0 rounded-md'
                        compact
                      />
                      <span className='flex-1 truncate font-light'>
                        {t.displayName}
                      </span>
                      {busy ? (
                        <LoadingSpinner className='h-4 w-4 border-primary-foreground border-b-transparent' />
                      ) : (
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0",
                            selected ? "opacity-100" : "opacity-0"
                          )}
                        />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
